'use client';

import React, { useEffect, useRef, useState } from 'react';
import { RedesignHomepageHero } from './hero';
import { ParallaxComponent } from './hero/ParallaxComponent';
import { useInView, useScroll, useTransform, motion } from 'framer-motion';
import { EmailCapture } from './EmailCapture';
import { SocialProof } from './SocialProof';
import { TypeRedesignHomepageFields } from '@/core/generated-types';
import { ScienceBehind } from './ScienceBehind';
import UGC from './UGC';
import { getTranslationsByIDs, getVideoUrls } from './UGC/utils';
import MoreToLove from './moreToLove';
import WhatIsIt from './WhatIsIt';
import { ObsessionVideo } from './ObsessionVideo';
import { ObsessionVideoSideBySide } from './ObsessionVideoSideBySide';
import { useIsVisible } from '@/hooks/useIsVisible';
import { CustomSchema } from '@/components';
import PlayKitsByAge from './PlayKitsByAge';
import useFeature from '@/hooks/useFeature';
import PickUpWhereYouLeftOffBanner from '@/components/common/PickUpWhereYouLeftOffBanner/PickUpWhereYouLeftOffBanner';
import { PromoRichText } from '@/components/common/HomepageHero/versions/PromoRichText';
import { ExtendedTypeHomepageHeroFields } from '@/components/common/HomepageHero/HomepageHero';
import classNames from 'classnames';
import { ColorfulBorder } from '../common/ColorfulBorder';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { HOMEPAGE_REDESIGN_IDS } from './types';

type TypeRedesignHomepage = {
  redesignPageFields: TypeRedesignHomepageFields;
};

const HomepageUS = ({ redesignPageFields }: TypeRedesignHomepage) => {
  const heroRef = useRef<HTMLDivElement | null>(null);
  const socialProofRef = useRef<HTMLDivElement | null>(null);

  const [heroHeight, setHeroHeight] = useState<number | null>(null);
  const isHeroInView = useIsVisible(heroRef);
  const isSocialInView = useInView(socialProofRef);
  const { scrollY } = useScroll();
  const isDesktop = useIsDesktop();
  const exploreThePlayKitsBtn = redesignPageFields.hero.fields.cta;
  const mobileHeroOpacity = useTransform(scrollY, [700, 750], [1, 0]);

  const isPickUpWhereYouLeftOffEnable = useFeature(
    'growth-enabled-pick-up-where-you-left-off-banner',
  );

  useEffect(() => {
    if (heroRef.current) {
      setHeroHeight(heroRef.current.offsetHeight);
    }
  }, []);

  const {
    ugc,
    more_to_love,
    scienceBehind,
    sideBySide,
    emailCapture,
    socialproof,
    experimentTranslations,
  } = getTranslationsByIDs({
    t: redesignPageFields.homepageRedesignTranslations,
    ids: [
      HOMEPAGE_REDESIGN_IDS.UGC,
      HOMEPAGE_REDESIGN_IDS.MORE_TO_LOVE,
      HOMEPAGE_REDESIGN_IDS.SCIENCE_BEHIND,
      HOMEPAGE_REDESIGN_IDS.SIDE_BY_SIDE,
      HOMEPAGE_REDESIGN_IDS.EMAIL_CAPTURE,
      HOMEPAGE_REDESIGN_IDS.SOCIAL_PROOF,
      HOMEPAGE_REDESIGN_IDS.EXPERIMENT_TRANSLATIONS,
    ],
  });

  const obssesionVideos = getVideoUrls(redesignPageFields.obsessionMedias);
  const sideBySideVideos = getVideoUrls(redesignPageFields.sideBySideMedias);

  return (
    <>
      {redesignPageFields.schema?.fields && (
        <CustomSchema {...redesignPageFields.schema.fields} />
      )}
      <motion.div
        ref={heroRef}
        className={classNames('sticky lg:relative', {
          'top-[72px]': !redesignPageFields.isPromoEnabled,
        })}
        // Hides hero after 700px of scrollY, for better performance.
        style={{
          opacity: isDesktop ? 'unset' : mobileHeroOpacity,
        }}
      >
        {isPickUpWhereYouLeftOffEnable && (
          <div className="block lg:hidden">
            <PickUpWhereYouLeftOffBanner />
          </div>
        )}
        {redesignPageFields.isPromoEnabled ? (
          <PromoRichText
            {...(redesignPageFields.hero
              .fields as ExtendedTypeHomepageHeroFields)}
          />
        ) : (
          <RedesignHomepageHero
            {...redesignPageFields.hero.fields}
            t={experimentTranslations}
            isHeroVisible={isHeroInView !== undefined ? isHeroInView : true}
          />
        )}
      </motion.div>

      {/* Needed to avoid flickering and hydration issues. */}
      {heroHeight && (
        // -mb-[100px] is needed to compensate translateY made by parallax effect (eliminates white space)
        // See reference in: https://github.com/lovevery-digital/lovevery-next/blob/1ecdf934b8ec08960e1e3536923c0c9dedf9cefa/src/experiments/homepage-redesign/hero/ParallaxComponent.tsx#L33
        <div
          className="h-full bg-white -mb-[100px]"
          style={{ marginTop: isDesktop ? 0 : `-4px` }}
        >
          <ParallaxComponent disable={!!redesignPageFields.isPromoEnabled}>
            <div className={!redesignPageFields.isPromoEnabled ? 'mt-1' : ''}>
              <ColorfulBorder />
            </div>

            <div className="z-10 bg-white">
              <PlayKitsByAge
                playKitsByAge={redesignPageFields.playKitsByAge}
                t={experimentTranslations}
              />

              <div className="mt-0">
                <SocialProof
                  entryTranslations={socialproof}
                  isVisible={isSocialInView}
                />
              </div>
              <WhatIsIt
                images={redesignPageFields.whatIsItImages}
                translations={redesignPageFields.homepageRedesignTranslations?.find(
                  (entry) => entry.fields.id === 'whatIsItTranslations',
                )}
              />
              <ObsessionVideo
                urlDesktop={obssesionVideos.videoDesktop}
                urlMobile={obssesionVideos.videoMobile}
              />
              <ScienceBehind
                cta={redesignPageFields.scienceBehindCta}
                t={scienceBehind}
              />
              <ObsessionVideoSideBySide
                t={sideBySide}
                cta={redesignPageFields.sideBySideCta}
                urlDesktop={sideBySideVideos.videoDesktop}
                urlMobile={sideBySideVideos.videoMobile}
              />
              <UGC
                page={redesignPageFields.ugcImages}
                cta={exploreThePlayKitsBtn}
                t={ugc}
              />
              {redesignPageFields.moreToLove && (
                <MoreToLove
                  images={redesignPageFields.moreToLove}
                  CTAs={[
                    redesignPageFields.giftLoveveryCta,
                    redesignPageFields.shopAllCta,
                  ]}
                  t={more_to_love}
                />
              )}
              <ColorfulBorder />
              <EmailCapture
                translations={emailCapture}
                image={redesignPageFields.emailCaptureImages}
                emailInput={redesignPageFields.emailInput}
              />
            </div>
          </ParallaxComponent>
        </div>
      )}
    </>
  );
};

export default HomepageUS;
